import {
    AuthFlowType,
    CognitoIdentityProviderClient,
    CognitoIdentityProviderClientConfig,
    InitiateAuthCommand,
    InitiateAuthCommandOutput,
} from '@aws-sdk/client-cognito-identity-provider'
import Cookies from 'js-cookie'
import jwtDecode from 'jwt-decode'
import getConfig from 'next/config'
import { removeCookies } from './logout'

interface AccessToken {
    exp: number
}

export async function hasValidAccessToken() {
    const access_token = Cookies.get('access_token') as string
    if (!access_token) {
        console.debug('no access_token found, checking if we can refresh and get a new one')
        return refreshToken()
    }

    const decoded_access_token: AccessToken = jwtDecode(access_token)
    const today = new Date()
    const minimum_exp = 1000 * 60 * 15  // 15 Minutes
    const expiry = (decoded_access_token.exp * 1000) - today.getTime()
    
    if (expiry < minimum_exp) {
        console.debug(`token is expiring in ${expiry} ms`)
        return refreshToken()
    }

    return true
}

async function refreshToken(): Promise<boolean> {
    const { COGNITO_CLIENT_ID: ClientId, COGNITO_REGION: region } =
        getConfig().publicRuntimeConfig
    const new_token = await getRefreshToken({ region, ClientId })

    if (!new_token?.AuthenticationResult) {
        removeCookies()
        if (window.location.pathname.startsWith('/dashboard')) {
            window.location.href = '/login/' + window.location.search
        }
        return false
    }

    const today = new Date()
    today.setHours(today.getHours() + 1)
    Cookies.set(
        'access_token',
        new_token?.AuthenticationResult.AccessToken as string,
        {
            path: '/',
            expires: today,
            sameSite: 'None',
            secure: true,
        }
    )
    Cookies.set(
        'id_token',
        new_token?.AuthenticationResult.IdToken as string,
        {
            path: '/',
            expires: today,
            sameSite: 'None',
            secure: true,
        }
    )
    return true
}

async function getRefreshToken(
    data: Record<string, string>
): Promise<InitiateAuthCommandOutput | undefined> {
    if (!Cookies.get('refresh_token')) return
    const { region, ClientId } = data
    const config: CognitoIdentityProviderClientConfig = {
        region,
    }
    const client = new CognitoIdentityProviderClient(config)
    const command = new InitiateAuthCommand({
        ClientId,
        AuthFlow: AuthFlowType.REFRESH_TOKEN,
        AuthParameters: {
            REFRESH_TOKEN: Cookies.get('refresh_token') as string,
        },
    })
    const res = await client.send(command)
    return res
}

export default hasValidAccessToken
